// src/pages/negative/NegativeListPage.jsx
import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Badge,
  Button,
  Card,
  Table,
  TextInput,
  Select,
  Pagination,
  Modal,
  Label,
  Alert,
} from "flowbite-react";
import {
  HiOutlineSearch,
  HiOutlineTrash,
  HiPaperAirplane,
  HiCheckCircle,
  HiShieldCheck,
  HiSparkles,
  HiOutlineX,
  HiOutlineExternalLink,
  HiOutlineEye,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { NEGATIVE } from "../../config/apiConfig";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { RxUpdate } from "react-icons/rx";
import { formatDocument } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";
import { Tooltip } from "react-tooltip";
import NegativePromoHero from "../common/NegativePromoHero";

const MOTIVOS_BAIXA = [
  {
    Codigo: "01",
    Motivo: "Pagamento da dívida",
    Descricao: "O devedor pagou a dívida.",
  },
  {
    Codigo: "03",
    Motivo: "Determinação Judicial",
    Descricao: "Baixa da anotação para cumprimento de determinação judicial.",
  },
  {
    Codigo: "05",
    Motivo: "Ordem Depto Jurídico",
    Descricao: "Ordem Depto Jurídico",
  },
  {
    Codigo: "06",
    Motivo: "Documento Roubado/BO",
    Descricao: "Documento Roubado",
  },
  {
    Codigo: "09",
    Motivo: "Falecimento do Devedor",
    Descricao: "Falecimento do Devedor",
  },
  { Codigo: "26", Motivo: "Pagamento Inibido", Descricao: "Pagamento Inibido" },
];

// helpers
const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const StatusBadge = ({ status }) => {
  const S = String(status || "").toUpperCase();
  const map = {
    ABERTO: "warning",
    ENVIADA: "info",
    ACEITA: "success",
    REJEITADA: "failure",
    BAIXADA: "gray",
  };
  return (
    <Badge color={map[S] || "indigo"} className="uppercase w-fit mx-auto">
      {S || "—"}
    </Badge>
  );
};

function useNegatives({
  companyId,
  q,
  status,
  dateFrom,
  dateTo,
  page,
  pageSize,
  orderBy,
  orderDir,
}) {
  return useQuery(
    [
      "negatives",
      {
        companyId,
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
      },
    ],
    async () => {
      const params = {
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
        companyId,
      };
      const { data } = await axios.get(NEGATIVE.LIST, { params });
      return data; // { items, total, page, pageSize, totalPages }
    },
    { keepPreviousData: true }
  );
}

function useNegativeDetail(id, open) {
  return useQuery(
    ["negative-detail", id],
    async () => {
      const { data } = await axios.get(NEGATIVE.GET(id));
      return data;
    },
    { enabled: !!id && open }
  );
}

export default function NegativeListPage() {
  const { user } = useApplication();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [orderBy, setOrderBy] = React.useState("id");
  const [orderDir, setOrderDir] = React.useState("DESC");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [statusLoadingIds, setStatusLoadingIds] = React.useState(new Set());
  const isRowLoading = (id) => statusLoadingIds.has(id);

  const syncToPage = (p) => {
    setCurrentPage(p);
    setPage(p);
  };

  const { data, isLoading } = useNegatives({
    companyId: user?.Company?.id,
    q: q || undefined,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    pageSize,
    orderBy,
    orderDir,
  });

  // mutations
  const sendExternal = useMutation(
    async (id) => {
      const { data } = await axios.post(NEGATIVE.SEND(id), {});
      return data;
    },
    { onSuccess: () => qc.invalidateQueries("negatives") }
  );

  const baixa = useMutation(
    async ({ id, motivoBaixa }) => {
      const { data } = await axios.post(NEGATIVE.BAIXA(id), {
        motivoBaixa,
      });
      return data;
    },
    {
      onSuccess: (_, { id }) => {
        qc.invalidateQueries("negatives");
        qc.invalidateQueries(["negative-detail", id]);
      },
    }
  );

  const searchStatus = async (negativeId) => {
    try {
      // liga loading para a linha específica
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.add(negativeId);
        return next;
      });
      await axios.get(`${NEGATIVE.SEARCH_STATUS}/${negativeId}`);
      // você pode fazer um invalidate aqui se a API atualizar o status no backend
      qc.invalidateQueries("negatives");
      qc.invalidateQueries(["negative-detail", negativeId]);
    } catch (error) {
      // opcional: toast/alert de erro
    } finally {
      // desliga loading da linha
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(negativeId);
        return next;
      });
    }
  };

  // modal baixa
  const [openBaixa, setOpenBaixa] = React.useState(false);
  const [baixaId, setBaixaId] = React.useState(null);
  const [motivoCodigo, setMotivoCodigo] = React.useState("");

  const openBaixaModal = (id) => {
    setBaixaId(id);
    setMotivoCodigo("");
    setOpenBaixa(true);
  };

  // modal detalhes
  const [openDetail, setOpenDetail] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useNegativeDetail(detailId, openDetail);

  const totalPages = data?.totalPages || 1;

  if (!user?.Company?.hasCV) {
    return (
      <div className="relative">
        <NegativePromoHero priceLabel="A partir de R$ 19,90 por CPF/CNPJ" />
      </div>
    );
  }

  return (
    <>
      <div className="p-4 md:p-6 mx-auto">
        <div className="mb-6 flex justify-between gap-2 md:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Negativações
            </h1>
            <p className="text-sm text-gray-500">
              Liste, filtre e gerencie as negativações dos seus clientes.
            </p>
          </div>
          <Button onClick={() => navigate("/negatives/create")}>
            Incluir negativação
          </Button>
        </div>

        {/* Filtros */}
        <Card className="mb-4 shadow-sm border-0">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <Label className="mb-1 block">Busca</Label>
              <TextInput
                icon={HiOutlineSearch}
                placeholder="Nome, documento, contrato..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div>
              <Label className="mb-1 block">Status</Label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="ABERTO">Aberto</option>
                <option value="ACEITO">Aceito</option>
                <option value="REJEITADO">Rejeitado</option>
                <option value="BAIXADO">Baixado</option>
              </Select>
            </div>

            <div>
              <Label className="mb-1 block">Venc. de</Label>
              <TextInput
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block">Venc. até</Label>
              <TextInput
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="mt-6 flex items-center gap-2">
              <Button onClick={() => syncToPage(1)} isProcessing={isLoading}>
                Aplicar filtros
              </Button>
            </div>
          </div>
        </Card>

        {/* Tabela */}
        <Card className="shadow-md border-0">
          <div className="text-sm text-gray-500">
            Total: <span className="font-medium">{data?.total || 0}</span>
          </div>
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>#</Table.HeadCell>
                <Table.HeadCell>Devedor</Table.HeadCell>
                <Table.HeadCell>Valor</Table.HeadCell>
                <Table.HeadCell className="text-center">
                  Dt. Inclusão
                </Table.HeadCell>
                <Table.HeadCell className="text-center">
                  Dt. Vencimento
                </Table.HeadCell>
                <Table.HeadCell className="text-center">
                  Dt. Disponível
                </Table.HeadCell>
                <Table.HeadCell className="text-center">Status</Table.HeadCell>
                <Table.HeadCell className="text-center">Ações</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {(data?.items || []).map((it) => (
                  <Table.Row key={it.id} className="bg-white">
                    <Table.Cell className="py-1 font-medium text-gray-900">
                      {it.id}
                    </Table.Cell>
                    <Table.Cell className="py-1 font-medium">
                      <div>{it.nome || "—"}</div>
                      <div className="text-xs">
                        {formatDocument(it.documentoDevedor)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>{brl(it.valor)}</Table.Cell>
                    <Table.Cell className="py-1 text-center">
                      {it.createdAt
                        ? format(new Date(it.createdAt), "dd/MM/yyyy")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell className="py-1 text-center">
                      {it.dataVencimento
                        ? format(new Date(it.dataVencimento), "dd/MM/yyyy")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell className="py-1 text-center">
                      {it.dataDisponivel
                        ? format(new Date(it.dataDisponivel), "dd/MM/yyyy")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      {isRowLoading(it.id) ? (
                        <div className="flex items-center justify-center py-1">
                          <span className="inline-block h-3 w-3 rounded-full border border-gray-300 border-t-transparent animate-spin" />
                        </div>
                      ) : (
                        <StatusBadge status={it.status} />
                      )}
                    </Table.Cell>
                    <Table.Cell className="py-1 text-center">
                      <div className="flex items-center gap-2 justify-center">
                        {!it.dataBaixa && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => {
                              searchStatus(it.id);
                            }}
                            data-tooltip-id="negTableStatus"
                            data-tooltip-content="Consultar Status"
                            disabled={isRowLoading(it.id)}
                          >
                            <RxUpdate
                              className={`h-4 w-4 ${
                                isRowLoading(it.id) ? "animate-spin" : ""
                              }`}
                            />
                          </Button>
                        )}
                        {/* Detalhes (ícone de olho) */}
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => {
                            setDetailId(it.id);
                            setOpenDetail(true);
                          }}
                          data-tooltip-id="negTableTips"
                          data-tooltip-content="Detalhes"
                          disabled={isRowLoading(it.id)}
                        >
                          <HiOutlineEye className="h-4 w-4" />
                        </Button>
                        {/* Baixa */}
                        {!it.dataBaixa && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openBaixaModal(it.id)}
                            data-tooltip-id="negTableTips"
                            data-tooltip-content="Dar Baixa"
                            disabled={isRowLoading(it.id)}
                          >
                            <HiCheckCircle className="h-4 w-4 text-green-500" />
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}

                {!isLoading && (data?.items || []).length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={7}
                      className="text-center text-gray-500 py-8"
                    >
                      Nenhum registro encontrado.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <Tooltip id="negTableTips" place="top" />
          <Tooltip id="negTableStatus" place="top" />

          {/* Paginação */}
          <div className="flex items-center justify-center mt-4">
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => syncToPage(p)}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          </div>
        </Card>

        {/* Modal Baixa */}
        <Modal
          show={openBaixa}
          size="lg"
          onClose={() => setOpenBaixa(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">
                Dar baixa na negativação
              </h3>

              <div>
                <Label className="mb-1 block">Motivo *</Label>
                <Select
                  value={motivoCodigo}
                  onChange={(e) => setMotivoCodigo(e.target.value)}
                  required
                >
                  <option value="">Selecione um motivo...</option>
                  {MOTIVOS_BAIXA.map((m) => (
                    <option key={m.Codigo} value={m.Codigo}>
                      {m.Codigo} — {m.Motivo}
                    </option>
                  ))}
                </Select>

                {/* bloco de descrição dinâmica */}
                {(() => {
                  const sel = MOTIVOS_BAIXA.find(
                    (m) => m.Codigo === motivoCodigo
                  );
                  if (!sel) return null;
                  return (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs text-gray-600">
                        Descrição: {sel.Descricao}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2">
                <Button color="light" onClick={() => setOpenBaixa(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    const motivoObj = MOTIVOS_BAIXA.find(
                      (m) => m.Codigo === motivoCodigo
                    );
                    if (!motivoObj) return;

                    // envie só o código OU código + motivo (você escolhe)
                    const motivoBaixa = motivoObj.Codigo; // ou `${motivoObj.Codigo} - ${motivoObj.Motivo}`

                    await baixa.mutateAsync({ id: baixaId, motivoBaixa });
                    setOpenBaixa(false);
                  }}
                  isProcessing={baixa.isLoading}
                  disabled={!motivoCodigo}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modal Detalhes */}
        <Modal
          show={openDetail}
          size="4xl"
          onClose={() => setOpenDetail(false)}
          className="backdrop-blur-sm"
        >
          <Modal.Header>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-indigo-100">
                <HiShieldCheck className="text-indigo-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">
                  Detalhes da negativação
                </div>
                <div className="text-lg font-semibold">
                  {detail
                    ? `#${detail.id} — ${detail.nome || "—"}`
                    : "Carregando..."}
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {isDetailLoading && (
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/3" />
                <div className="h-24 bg-gray-100 animate-pulse rounded" />
                <div className="h-40 bg-gray-100 animate-pulse rounded" />
              </div>
            )}

            {!isDetailLoading && isDetailError && (
              <Alert color="failure">
                Não foi possível carregar os detalhes.
              </Alert>
            )}

            {!isDetailLoading && detail && (
              <div>
                {/* Status + Valor + Vencimento */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="mt-1">{detail.status}</div>
                  </div>
                  <di className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Valor</div>
                    <div className="mt-1 text-lg font-semibold">
                      {brl(detail.valor)}
                    </div>
                  </di>
                  <div className=" p-2 rounded-lg border border-gray-200 shadow-sm">
                    <div className="text-xs text-gray-500">Vencimento</div>
                    <div className="mt-1 text-lg font-semibold">
                      {detail.dataVencimento
                        ? format(new Date(detail.dataVencimento), "dd/MM/yyyy")
                        : "—"}
                    </div>
                  </div>
                </div>

                {/* Dados principais */}
                <div className="mt-4 mb-1">
                  <div className="text-sm font-semibold">Devedor</div>
                  <div className="text-xs text-gray-500">
                    Documento, contrato e natureza do título
                  </div>
                </div>
                <div className="rounded-lg px-2 py-3 shadow-sm border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Nome</div>
                      <div className="font-medium">{detail.nome || "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Documento</div>
                      <div className="font-mono">
                        {formatDocument(detail.documentoDevedor)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Contrato</div>
                      <div className="font-medium">
                        {detail.contrato || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">
                        Natureza (código)
                      </div>
                      <div className="font-medium">
                        {detail.codigoNatureza || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Criado em</div>
                      <div className="font-medium">
                        {detail.createdAt
                          ? format(
                              new Date(detail.createdAt),
                              "dd/MM/yyyy HH:mm"
                            )
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Atualizado em</div>
                      <div className="font-medium">
                        {detail.updatedAt
                          ? format(
                              new Date(detail.updatedAt),
                              "dd/MM/yyyy HH:mm"
                            )
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Endereço & Contato */}
                <div className="mb-1 mt-4">
                  <div className="text-sm font-semibold">
                    Endereço & Contato
                  </div>
                  <div className="text-xs text-gray-500">
                    Dados de localização e meios de contato
                  </div>
                </div>
                <div className="rounded-lg px-2 py-3 shadow-sm border border-gray-200 mb-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-500">Endereço</div>
                      <div className="font-medium text-sm">
                        {detail.endereco || "—"}
                        {detail.bairro ? `, ${detail.bairro}` : ""}
                        {detail.cidade ? ` - ${detail.cidade}` : ""}
                        {detail.uf ? `/${detail.uf}` : ""}
                        {detail.cep ? ` — CEP ${detail.cep}` : ""}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Contato</div>
                      <div className="font-medium text-sm">
                        {detail.ddd && detail.telefone
                          ? `(${detail.ddd}) ${detail.telefone}`
                          : ""}
                        {detail.email ? ` · ${detail.email}` : ""}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ações rápidas dentro do modal */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-gray-500">
                    ID interno: <span className="font-mono">{detail.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      color="light"
                      onClick={() => openBaixaModal(detail.id)}
                    >
                      Dar baixa
                    </Button>
                    <Button color="light" onClick={() => setOpenDetail(false)}>
                      Fechar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>

      {/* Tela “Em breve” (quando hasCV) */}
      {/* Mantida no early-return acima */}
    </>
  );
}
